import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { ClipboardList, Truck, HardDrive, FileCheck } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const STEPS = [
  {
    icon: ClipboardList,
    title: "Book & Audit",
    description: "Tell us what equipment you have and schedule a pickup.",
    testId: "process-step-book",
  },
  {
    icon: Truck,
    title: "Secure Collection",
    description: "Our team safely removes the assets from your premises.",
    testId: "process-step-collection",
  },
  {
    icon: HardDrive,
    title: "Data Destruction",
    description: "All data-bearing devices are sanitized or physically destroyed.",
    testId: "process-step-destruction",
  },
  {
    icon: FileCheck,
    title: "Reporting & Certificates",
    description: "Receive your ESG recycling report and Certificate of Data Destruction.",
    testId: "process-step-reporting",
  },
];

export default function Process() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.75", "end 0.6"],
  });
  const lineScale = useSpring(scrollYProgress, { stiffness: 60, damping: 20 });

  return (
    <section
      id="data-security"
      className="noise-overlay relative overflow-hidden bg-navy py-24 sm:py-32"
      data-testid="process-section"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute top-0 right-0 select-none font-display text-[18vw] font-black leading-none text-outline"
      >
        04
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: EASE }}
              className="lg:sticky lg:top-32"
            >
              <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-eco" data-testid="process-overline">
                Chapter 02 — How It Works
              </p>
              <h2 className="font-display text-4xl font-semibold tracking-tight text-white sm:text-5xl" data-testid="process-heading">
                From loading dock to
                <span className="text-eco"> certified destruction.</span>
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
                A documented chain of custody at every stage. Your data never leaves sight without
                being verifiably destroyed — and you get the paperwork to prove it.
              </p>

              <motion.div
                initial={{ clipPath: "inset(0 100% 0 0)" }}
                whileInView={{ clipPath: "inset(0 0% 0 0)" }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1.1, ease: EASE, delay: 0.2 }}
                className="mt-10 hidden overflow-hidden rounded-2xl border border-white/10 lg:block"
              >
                <img
                  src="/images/process-collection.png"
                  alt="White-glove IT asset collection at a corporate loading dock"
                  className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105"
                  data-testid="process-image"
                />
              </motion.div>
            </motion.div>
          </div>

          <div ref={ref} className="relative lg:col-span-7">
            <div aria-hidden className="absolute left-6 top-2 bottom-2 w-px bg-white/10 sm:left-8" />
            <motion.div
              aria-hidden
              style={{ scaleY: lineScale }}
              className="absolute left-6 top-2 bottom-2 w-px origin-top bg-eco shadow-[0_0_12px_rgba(52,199,123,0.6)] sm:left-8"
            />

            <div className="flex flex-col gap-14 sm:gap-20" data-testid="process-steps">
              {STEPS.map((step, i) => (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, x: 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.8, ease: EASE, delay: 0.05 * i }}
                  className="relative flex gap-6 pl-16 sm:gap-8 sm:pl-24"
                  data-testid={step.testId}
                >
                  <span className="absolute left-0 top-0 flex h-12 w-12 items-center justify-center rounded-full border border-eco/40 bg-navy-800 font-display text-sm font-semibold text-eco shadow-[0_0_24px_rgba(52,199,123,0.25)] sm:h-16 sm:w-16 sm:text-base">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="rounded-2xl border border-white/10 bg-navy-800/60 p-6 backdrop-blur-sm transition-[border-color,background-color] duration-500 hover:border-eco/30 hover:bg-navy-800 sm:p-8">
                    <div className="mb-3 flex items-center gap-3">
                      <step.icon className="h-5 w-5 text-eco" strokeWidth={2} aria-hidden />
                      <h3 className="font-display text-xl font-semibold tracking-tight text-white sm:text-2xl">
                        {step.title}
                      </h3>
                    </div>
                    <p className="text-sm leading-relaxed text-slate-400 sm:text-base">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
